import { SaxesParser } from "saxes";
import type * as Monaco from "monaco-editor";

export interface XmlError {
  line: number;
  column: number;
  message: string;
  severity: "error" | "warning";
}

export interface XmlContext {
  /** Stack of open tags */
  tagStack: string[];
  /** Current tag being edited (if any) */
  currentTag?: string;
  /** Whether we're inside a tag (between < and >) */
  inTag: boolean;
  /** Whether we're in an attribute value */
  inAttributeValue: boolean;
  /** Current attribute name being edited */
  currentAttribute?: string;
}

// Common XML/XSL elements for completion
const XML_ELEMENTS = ["xml", "root", "item", "element", "data", "value", "name", "id"];

const XSL_ELEMENTS = [
  "xsl:stylesheet",
  "xsl:template",
  "xsl:apply-templates",
  "xsl:call-template",
  "xsl:value-of",
  "xsl:for-each",
  "xsl:if",
  "xsl:choose",
  "xsl:when",
  "xsl:otherwise",
  "xsl:variable",
  "xsl:param",
  "xsl:with-param",
  "xsl:output",
  "xsl:import",
  "xsl:include",
  "xsl:copy",
  "xsl:copy-of",
  "xsl:text",
  "xsl:element",
  "xsl:attribute",
  "xsl:comment",
  "xsl:processing-instruction",
  "xsl:sort",
  "xsl:key",
  "xsl:number",
  "xsl:message",
  "xsl:fallback",
  "xsl:decimal-format",
  "xsl:namespace-alias",
  "xsl:attribute-set",
  "xsl:strip-space",
  "xsl:preserve-space",
];

const XSL_ATTRIBUTES: Record<string, string[]> = {
  "xsl:stylesheet": ["version", "xmlns:xsl", "exclude-result-prefixes", "extension-element-prefixes"],
  "xsl:template": ["match", "name", "priority", "mode"],
  "xsl:apply-templates": ["select", "mode"],
  "xsl:call-template": ["name"],
  "xsl:value-of": ["select", "disable-output-escaping"],
  "xsl:for-each": ["select"],
  "xsl:if": ["test"],
  "xsl:choose": [],
  "xsl:when": ["test"],
  "xsl:otherwise": [],
  "xsl:variable": ["name", "select"],
  "xsl:param": ["name", "select"],
  "xsl:with-param": ["name", "select"],
  "xsl:output": ["method", "encoding", "indent", "omit-xml-declaration", "standalone", "doctype-public", "doctype-system", "media-type"],
  "xsl:import": ["href"],
  "xsl:include": ["href"],
  "xsl:copy": ["use-attribute-sets"],
  "xsl:copy-of": ["select"],
  "xsl:text": ["disable-output-escaping"],
  "xsl:element": ["name", "namespace", "use-attribute-sets"],
  "xsl:attribute": ["name", "namespace"],
  "xsl:sort": ["select", "order", "data-type", "case-order", "lang"],
  "xsl:key": ["name", "match", "use"],
  "xsl:number": ["level", "count", "from", "value", "format", "lang", "letter-value", "grouping-separator", "grouping-size"],
  "xsl:message": ["terminate"],
};

const COMMON_ATTRIBUTES = ["id", "class", "name", "type", "value", "href", "src", "style"];

/**
 * Validate XML using saxes parser
 */
export function validateXml(xml: string): XmlError[] {
  const errors: XmlError[] = [];

  if (!xml.trim()) {
    return errors;
  }

  const parser = new SaxesParser({ xmlns: true });

  parser.on("error", (err) => {
    errors.push({
      line: parser.line,
      column: parser.column,
      message: err.message.replace(/^.*?:\s*/, ""), // Clean up error message
      severity: "error",
    });
  });

  try {
    parser.write(xml).close();
  } catch (e) {
    // Parser already reported error via event
  }

  return errors;
}

/**
 * Parse the current editing context from XML content and cursor position
 */
export function getXmlContext(xml: string, offset: number): XmlContext {
  const context: XmlContext = {
    tagStack: [],
    inTag: false,
    inAttributeValue: false,
  };

  // Simple state machine to track context
  let i = 0;
  let inTag = false;
  let inClosingTag = false;
  let inAttributeValue = false;
  let quoteChar = "";
  let currentTag = "";
  let currentAttr = "";
  let tagStart = -1;
  let attrStart = -1;

  while (i < offset && i < xml.length) {
    const char = xml[i];
    const nextChar = xml[i + 1];

    if (inAttributeValue) {
      if (char === quoteChar) {
        inAttributeValue = false;
        quoteChar = "";
        currentAttr = "";
      }
    } else if (inTag) {
      if (char === ">") {
        inTag = false;
        if (!inClosingTag && !xml.substring(tagStart, i).endsWith("/")) {
          // Opening tag - push to stack
          const tagName = currentTag.split(/\s/)[0];
          if (tagName) {
            context.tagStack.push(tagName);
          }
        } else if (inClosingTag) {
          // Closing tag - pop from stack
          context.tagStack.pop();
        }
        currentTag = "";
        inClosingTag = false;
      } else if (char === '"' || char === "'") {
        inAttributeValue = true;
        quoteChar = char;
      } else if (char === "=" && !inAttributeValue) {
        // Found attribute name before =
        const match = xml.substring(tagStart, i).match(/(\S+)\s*$/);
        if (match && match[1]) {
          currentAttr = match[1];
          attrStart = i - match[1].length;
        }
      } else {
        currentTag += char;
      }
    } else {
      if (char === "<") {
        inTag = true;
        tagStart = i + 1;
        currentTag = "";
        if (nextChar === "/") {
          inClosingTag = true;
          i++; // Skip the /
        } else if (nextChar === "?" || nextChar === "!") {
          // Processing instruction or comment/DOCTYPE
          inTag = false;
          // Skip to end
          const endIndex = xml.indexOf(">", i);
          if (endIndex !== -1) {
            i = endIndex;
          }
        }
      }
    }
    i++;
  }

  // Set current state based on where cursor is
  context.inTag = inTag;
  context.inAttributeValue = inAttributeValue;
  if (inTag && currentTag) {
    const tagName = currentTag.split(/\s/)[0];
    if (tagName) {
      context.currentTag = tagName;
    }
  }
  if (currentAttr) {
    context.currentAttribute = currentAttr;
  }

  return context;
}

/**
 * Get completion items for XML/XSL
 */
export function getCompletions(
  context: XmlContext,
  isXsl: boolean,
  monaco: typeof Monaco,
  range: Monaco.IRange
): Monaco.languages.CompletionItem[] {
  const items: Monaco.languages.CompletionItem[] = [];

  if (context.inAttributeValue) {
    // Inside attribute value - provide value suggestions based on attribute
    if (context.currentAttribute === "method") {
      ["xml", "html", "text"].forEach((value) => {
        items.push({
          label: value,
          kind: monaco.languages.CompletionItemKind.Value,
          insertText: value,
          range,
        });
      });
    } else if (context.currentAttribute === "version") {
      ["1.0", "2.0", "3.0"].forEach((value) => {
        items.push({
          label: value,
          kind: monaco.languages.CompletionItemKind.Value,
          insertText: value,
          range,
        });
      });
    } else if (context.currentAttribute === "indent" || context.currentAttribute === "omit-xml-declaration") {
      ["yes", "no"].forEach((value) => {
        items.push({
          label: value,
          kind: monaco.languages.CompletionItemKind.Value,
          insertText: value,
          range,
        });
      });
    } else if (context.currentAttribute === "order") {
      ["ascending", "descending"].forEach((value) => {
        items.push({
          label: value,
          kind: monaco.languages.CompletionItemKind.Value,
          insertText: value,
          range,
        });
      });
    }
    return items;
  }

  if (context.inTag && context.currentTag) {
    // Inside a tag - provide attribute completions
    const tagName = context.currentTag;
    const attrs = XSL_ATTRIBUTES[tagName] || COMMON_ATTRIBUTES;

    attrs.forEach((attr) => {
      items.push({
        label: attr,
        kind: monaco.languages.CompletionItemKind.Property,
        insertText: `${attr}="$1"`,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range,
      });
    });
    return items;
  }

  // Provide element completions
  const elements = isXsl ? [...XSL_ELEMENTS, ...XML_ELEMENTS] : XML_ELEMENTS;

  // Add elements from the tag stack (for closing tags or similar elements)
  context.tagStack.forEach((tag) => {
    if (!elements.includes(tag)) {
      elements.push(tag);
    }
  });

  elements.forEach((element) => {
    // Self-closing snippet
    items.push({
      label: element,
      kind: monaco.languages.CompletionItemKind.Class,
      insertText: `${element}$1/>`,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      detail: "Self-closing tag",
      range,
    });

    // Opening/closing tag pair
    items.push({
      label: `${element}...`,
      kind: monaco.languages.CompletionItemKind.Class,
      insertText: `${element}$1>$2</${element}>`,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      detail: "Tag pair",
      range,
    });
  });

  // Add closing tag suggestion if there are open tags
  if (context.tagStack.length > 0) {
    const lastTag = context.tagStack[context.tagStack.length - 1];
    items.unshift({
      label: `/${lastTag}>`,
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: `/${lastTag}>`,
      detail: "Close current tag",
      sortText: "0", // Sort first
      range,
    });
  }

  return items;
}

/**
 * Convert XmlErrors to Monaco markers
 */
export function errorsToMarkers(
  errors: XmlError[],
  monaco: typeof Monaco
): Monaco.editor.IMarkerData[] {
  return errors.map((error) => ({
    severity: error.severity === "error"
      ? monaco.MarkerSeverity.Error
      : monaco.MarkerSeverity.Warning,
    startLineNumber: error.line,
    startColumn: error.column,
    endLineNumber: error.line,
    endColumn: error.column + 1,
    message: error.message,
    source: "xml-validator",
  }));
}
